// Cosmetic active ingredients offered by LAMKO (personal-care line).
// Public, non-commercial data only: identity, function, physical form, typical specification.
// No supplier, capacity or pricing information belongs in this file.
// LAMKO is the supplier of record; production is by audited partner manufacturers. Never state that LAMKO manufactures these in-house.

export const COSMETIC_CATEGORIES = [
  { key: 'uv', label: 'UV filters' },
  { key: 'scalp', label: 'Scalp & hair care' },
  { key: 'brightening', label: 'Brightening' },
  { key: 'preservation', label: 'Preservation & antioxidants' },
];

export const AVAILABILITY = {
  regular: { label: 'Regular supply', cls: 'bg-teal-50 text-teal-800 border-teal-200' },
  request: { label: 'On request', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  mto: { label: 'Made to order', cls: 'bg-orange-50 text-orange-800 border-orange-200' },
  dev: { label: 'Under development', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

export const COSMETIC_ACTIVES = [
  {
    slug: 'ethylhexyl-triazone',
    inci: 'Ethylhexyl Triazone',
    short: 'EHT',
    iupac: '2-Ethylhexyl 4-[[4,6-bis[4-(2-ethylhexoxycarbonyl)anilino]-1,3,5-triazin-2-yl]amino]benzoate',
    cas: '88122-99-0',
    formula: 'C48H66N6O6',
    mw: '823.07 g/mol',
    category: 'uv',
    function: 'Oil-soluble UVB filter with very high specific extinction; photostable, low use level for high SPF.',
    appearance: 'White to light-yellow powder',
    availability: 'regular',
    specs: [
      ['Purity (HPLC)', '≥ 99.5 %'],
      ['Assay', '98.0 to 103.0 %'],
      ['Melting point', '123 to 132 °C'],
      ['E (1 %, 1 cm) at 314 nm', '≥ 1500'],
      ['Water (KF)', '≤ 0.50 %'],
    ],
    applications: ['Sun care (high SPF)', 'Daily-wear UV protection', 'Colour cosmetics with SPF'],
    regulatory: 'Listed UV filter in the EU (Annex VI) and Korea (MFDS restricted-use list) with market-specific maximum levels. Not an approved sunscreen active in the USA.',
  },
  {
    slug: 'diethylhexyl-butamido-triazone',
    inci: 'Diethylhexyl Butamido Triazone',
    short: 'DHBT · Iscotrizinol',
    iupac: '2-Ethylhexyl 4-[[4-[4-(tert-butylcarbamoyl)anilino]-6-[4-(2-ethylhexoxycarbonyl)anilino]-1,3,5-triazin-2-yl]amino]benzoate',
    cas: '154702-15-5',
    formula: 'C44H59N7O5',
    mw: '765.98 g/mol',
    category: 'uv',
    function: 'Oil-soluble broad UVB with UVA II coverage; highly photostable, helps stabilise other filters.',
    appearance: 'White to almost white powder',
    availability: 'regular',
    specs: [
      ['Assay', '≥ 98.0 %'],
      ['Purity (HPLC)', '≥ 98.0 %'],
      ['Specific extinction', '≥ 1470'],
      ['Colour (APHA, solution)', '≤ 650'],
      ['Heavy metals', '≤ 10 ppm'],
      ['Trace metals (each)', '≤ 2 ppm'],
    ],
    applications: ['Sun care', 'Photostable filter systems', 'Anti-ageing day care'],
    regulatory: 'Listed UV filter in the EU (Annex VI) and Korea (MFDS restricted-use list) with market-specific maximum levels. Not an approved sunscreen active in the USA.',
  },
  {
    slug: 'padimate-o',
    inci: 'Ethylhexyl Dimethyl PABA',
    short: 'Padimate O',
    iupac: '2-Ethylhexyl 4-(dimethylamino)benzoate',
    cas: '21245-02-3',
    formula: 'C17H27NO2',
    mw: '277.40 g/mol',
    category: 'uv',
    function: 'Oil-soluble UVB filter; liquid, easy to formulate, water resistant.',
    appearance: 'Pale-yellow, clear liquid',
    availability: 'regular',
    specs: [
      ['Assay', '≥ 98.0 %'],
      ['Refractive index (20 °C)', 'Report'],
      ['Acid value', '≤ 1.0 mg KOH/g'],
      ['Colour (APHA)', 'Report'],
    ],
    applications: ['Sun care', 'Water-resistant formulations'],
    regulatory: 'Permitted UV filter in the EU (Annex VI, 8 %), USA (FDA monograph, 8 %) and Korea (8 %).',
  },
  {
    slug: 'diethylamino-hydroxybenzoyl-hexyl-benzoate',
    inci: 'Diethylamino Hydroxybenzoyl Hexyl Benzoate',
    short: 'DHHB',
    iupac: 'Hexyl 2-[4-(diethylamino)-2-hydroxybenzoyl]benzoate',
    cas: '302776-68-7',
    formula: 'C24H31NO4',
    mw: '397.51 g/mol',
    category: 'uv',
    function: 'Oil-soluble UVA I filter; photostable, compatible with organic and mineral filters.',
    appearance: 'Light-yellow powder',
    availability: 'request',
    specs: [['Purity (HPLC)', '≥ 99.0 %'], ['Melting point', '52 to 58 °C'], ['Loss on drying', '≤ 0.5 %']],
    applications: ['Broad-spectrum sun care', 'Anti-ageing day care'],
    regulatory: 'Listed UV filter in the EU (Annex VI), Korea (MFDS restricted-use list) and Japan with market-specific maximum levels. Not an approved sunscreen active in the USA.',
  },
  {
    slug: 'bis-ethylhexyloxyphenol-methoxyphenyl-triazine',
    inci: 'Bis-Ethylhexyloxyphenol Methoxyphenyl Triazine',
    short: 'BEMT · Bemotrizinol',
    iupac: '2,2′-[6-(4-Methoxyphenyl)-1,3,5-triazine-2,4-diyl]bis{5-[(2-ethylhexyl)oxy]phenol}',
    cas: '187393-00-6',
    formula: 'C38H49N3O5',
    mw: '627.81 g/mol',
    category: 'uv',
    function: 'Oil-soluble broad-spectrum UVA/UVB filter; very photostable, stabilises other filters.',
    appearance: 'Light-yellow powder',
    availability: 'request',
    specs: [['Purity (HPLC)', '≥ 99.0 %'], ['Melting point', '78 to 84 °C'], ['Loss on drying', '≤ 0.5 %']],
    applications: ['Broad-spectrum sun care', 'High-SPF systems', 'Daily UV protection'],
    regulatory: 'Listed UV filter in the EU (Annex VI) and Korea (MFDS restricted-use list) with market-specific maximum levels. Not an approved sunscreen active in the USA.',
  },
  {
    slug: 'methylene-bis-benzotriazolyl-tetramethylbutylphenol',
    inci: 'Methylene Bis-Benzotriazolyl Tetramethylbutylphenol',
    short: 'MBBT · Bisoctrizole',
    iupac: '2,2′-Methylenebis[6-(2H-benzotriazol-2-yl)-4-(1,1,3,3-tetramethylbutyl)phenol]',
    cas: '103597-45-1',
    formula: 'C41H50N6O2',
    mw: '658.87 g/mol',
    category: 'uv',
    function: 'Broad-spectrum UVA/UVB filter acting by absorption, reflection and scattering; used as a micro-fine particulate.',
    appearance: 'White to off-white powder (also as aqueous dispersion)',
    availability: 'request',
    specs: [['Purity (HPLC)', '≥ 99.0 %'], ['Particle size (dispersion)', 'Report'], ['Loss on drying', '≤ 0.5 %']],
    applications: ['Broad-spectrum sun care', 'Mineral-organic hybrid systems'],
    regulatory: 'Listed UV filter in the EU (Annex VI) and Korea (MFDS restricted-use list) with market-specific maximum levels. Not an approved sunscreen active in the USA.',
  },
  {
    slug: 'terephthalylidene-dicamphor-sulfonic-acid',
    inci: 'Terephthalylidene Dicamphor Sulfonic Acid',
    short: 'TDSA · Ecamsule',
    iupac: '3,3′-(1,4-Phenylenedimethylidene)bis(7,7-dimethyl-2-oxobicyclo[2.2.1]heptane-1-methanesulfonic acid)',
    cas: '92761-26-7',
    formula: 'C28H34O8S2',
    mw: '562.69 g/mol',
    category: 'uv',
    function: 'Water-soluble UVA filter; photostable, for the aqueous phase of broad-spectrum systems.',
    appearance: 'White to off-white powder',
    availability: 'dev',
    specs: [['Purity (HPLC)', 'Target ≥ 98.0 %'], ['Specification', 'Under development; available on request']],
    applications: ['Broad-spectrum sun care (aqueous phase)', 'Anti-ageing day care'],
    regulatory: 'Listed UV filter in the EU (Annex VI) and Korea (MFDS restricted-use list). In the USA only within specific approved products.',
  },
  {
    slug: 'piroctone-olamine',
    inci: 'Piroctone Olamine',
    short: 'Hydroxypyridone anti-dandruff active',
    iupac: '1-Hydroxy-4-methyl-6-(2,4,4-trimethylpentyl)pyridin-2(1H)-one, compound with 2-aminoethanol (1:1)',
    cas: '68890-66-4',
    formula: 'C16H30N2O3',
    mw: '298.42 g/mol',
    category: 'scalp',
    function: 'Anti-dandruff and scalp-care active (antifungal, chelating) for rinse-off and leave-on scalp products.',
    appearance: 'White to slightly yellow crystalline powder',
    availability: 'regular',
    specs: [
      ['Assay', '98.0 to 101.5 %'],
      ['Purity (HPLC)', '≥ 99.0 %'],
      ['Ethanolamine content', '20.05 to 20.9 %'],
      ['Melting point', '130 to 136 °C'],
      ['Loss on drying', '≤ 0.30 %'],
    ],
    applications: ['Anti-dandruff shampoo', 'Scalp tonics and serums', 'Rinse-off and leave-on scalp care'],
    regulatory: 'Permitted preservative in the EU (Annex V) and Korea (MFDS list: 1.0 % rinse-off, 0.5 % leave-on). Efficacy claims on finished products are the responsibility of the brand owner and are market-specific.',
  },
  {
    slug: '4-ethylresorcinol',
    inci: '4-Ethylresorcinol',
    short: 'Brightening active',
    iupac: '4-Ethylbenzene-1,3-diol',
    cas: '2896-60-8',
    formula: 'C8H10O2',
    mw: '138.16 g/mol',
    category: 'brightening',
    function: 'Tyrosinase-inhibiting brightening and even-tone active; resorcinol class.',
    appearance: 'White to off-white crystalline powder',
    availability: 'mto',
    specs: [['Purity (HPLC)', '≥ 99.0 %'], ['Melting point', '96 to 100 °C'], ['Loss on drying', '≤ 0.5 %']],
    applications: ['Brightening serums', 'Dark-spot correctors', 'Even-tone day care'],
    regulatory: 'Check market-specific requirements; documentation on request.',
  },
  {
    slug: '3-5-di-tert-butyl-4-hydroxybenzyl-alcohol',
    inci: '3,5-Di-tert-butyl-4-hydroxybenzyl Alcohol',
    short: 'DTDHBA',
    iupac: '2,6-Di-tert-butyl-4-(hydroxymethyl)phenol',
    cas: '88-26-6',
    formula: 'C15H24O2',
    mw: '236.35 g/mol',
    category: 'preservation',
    function: 'Hindered-phenol antioxidant and preservation support; protects oils and actives against oxidation.',
    appearance: 'White crystalline powder',
    availability: 'mto',
    specs: [['Purity (GC/HPLC)', '≥ 99.0 %'], ['Melting point', '138 to 142 °C'], ['Loss on drying', '≤ 0.3 %']],
    applications: ['Oil-phase stabilisation', 'Preservation systems', 'Colour and odour protection'],
    regulatory: 'Check market-specific requirements; documentation on request.',
  },
  {
    slug: 'ethylhexylglycerin',
    inci: 'Ethylhexylglycerin',
    short: 'Preservative booster · deodorant active',
    iupac: '3-[(2-Ethylhexyl)oxy]propane-1,2-diol',
    cas: '70445-33-9',
    formula: 'C11H24O3',
    mw: '204.31 g/mol',
    category: 'preservation',
    function: 'Multifunctional skin-conditioning agent, preservative booster and deodorant active; enhances efficacy of traditional preservatives.',
    appearance: 'Colourless, clear liquid',
    availability: 'mto',
    specs: [['Purity (GC)', '≥ 99.0 %'], ['Water (KF)', '≤ 0.5 %'], ['Colour (APHA)', '≤ 50'], ['Acid value', '≤ 0.5 mg KOH/g']],
    applications: ['Preservative boosting (with phenoxyethanol etc.)', 'Deodorants', 'Leave-on skin care', 'Wet wipes'],
    regulatory: 'Widely used globally; not a listed preservative, functions as a booster and skin-conditioning agent.',
  },
];

export const findActive = (slug) => COSMETIC_ACTIVES.find((a) => a.slug === slug);

// Options for the combined cosmetic-actives enquiry form (CRDMO + CDMO).
export const COSMETICS_OPTIONS = {
  companyType: ['Brand owner', 'OEM / ODM manufacturer', 'Raw-material distributor', 'Formulation laboratory', 'CRO / research institute', 'Other (please specify)'],
  serviceScope: [
    'Feasibility and route scouting (CRDMO)',
    'Lab-scale samples for evaluation',
    'Process development and scale-up (CDMO)',
    'Pilot batch (kg scale)',
    'Commercial supply of an existing active',
    'Purification or impurity reduction of your material',
    'Custom derivative or new molecule',
    'Regulatory documentation support (TDS / SDS / COA)',
  ],
  ingredientCategory: ['UV filter', 'Scalp and hair care', 'Brightening / even tone', 'Preservation and antioxidants', 'Emollient / naturally derived', 'Peptide / biotech active', 'Other (please specify)'],
  catalogueItem: ['Not listed / custom molecule', ...COSMETIC_ACTIVES.map((a) => `${a.inci} (CAS ${a.cas})`)],
  gradeRequired: ['Cosmetic grade', 'Cosmetic grade with Halal certificate', 'Cosmetic grade with vegan statement', 'Pharma-like GMP documentation', 'Not sure yet'],
  sampleQuantity: ['Not needed', '10 to 100 g', '100 g to 1 kg', '1 to 10 kg'],
  commercialQuantity: ['Not yet known', 'Below 100 kg per year', '100 kg to 1 MT per year', '1 to 10 MT per year', '10 to 50 MT per year', 'Above 50 MT per year'],
  timeline: ['Samples within 1 month', '1 to 3 months', '3 to 6 months', 'More than 6 months / planning stage'],
  markets: ['Korea (MFDS)', 'European Union', 'United States', 'Japan', 'China (NMPA)', 'ASEAN', 'Middle East (Halal)', 'Other'],
  documents: ['Technical data sheet (TDS)', 'Safety data sheet (SDS)', 'Certificate of analysis (COA)', 'Stability data', 'Allergen and impurity statements', 'Halal / vegan certificates', 'REACH registration status', 'Other'],
  existingSupplier: ['Yes, looking for a second source', 'Yes, looking to switch', 'No, new project', 'Prefer not to say'],
  structureAvailable: ['Yes, I can share it after the CDA', 'It is a known molecule (INCI / CAS given)', 'Not yet'],
};
